/**
 * Classification Analytics API Client
 * Handles skill execution and classification log analytics
 */

import { apiClient } from './client';

// ============= Types =============

export interface SkillExecutionRecord {
  id: string;
  user_id: string;
  topic_id: string;
  skill_id: string;
  user_query: string;
  match_source: 'auto' | 'tool';
  matched_rule?: string;
  confidence?: number;
  created_at: string;
}

export interface SkillExecutionStats {
  total_executions: number;
  by_skill: Record<string, number>;
  by_source: Record<string, number>;
  by_match_layer: Record<string, number>;
  avg_confidence_by_skill: Record<string, number>;
  time_range_hours: number;
}

export interface ClassificationLogRecord {
  id: string;
  user_id: string;
  topic_id?: string;
  agent_type: string;
  user_query: string;
  query_length?: number;
  response_type?: 'quick' | 'deep';
  response_type_confidence?: number;
  response_type_matched_rule?: string;
  response_type_latency_ms?: number;
  classification_method?: string;
  // Skill classification metrics
  skill_match_layer?: 'rule' | 'semantic' | 'llm' | 'combined_llm' | 'none';
  skill_rule_match_time_ms?: number;
  skill_llm_match_time_ms?: number;
  total_classification_time_ms?: number;
  matched_skills?: string[];
  matched_skill_rules?: string[];
  skills_configured?: number;
  prompt_injection_chars?: number;
  created_at: string;
}

export interface ClassificationStats {
  total_classifications: number;
  by_response_type: Record<string, number>;
  by_method: Record<string, number>;
  avg_confidence_by_type: Record<string, number>;
  avg_latency_by_method: Record<string, number>;
  avg_query_length: number;
  time_range_hours: number;
  // Skill classification stats
  by_skill_match_layer: Record<string, number>;
  by_matched_skill: Record<string, number>;
  avg_rule_match_time_ms?: number;
  avg_llm_match_time_ms?: number;
  avg_total_time_ms?: number;
  avg_prompt_injection_chars?: number;
}

export interface QueryParams {
  start_date?: string;
  end_date?: string;
  user_id?: string;
  skill_id?: string;
  response_type?: string;
  classification_method?: string;
  agent_type?: string;
  match_source?: string;
  min_confidence?: number;
  limit?: number;
  offset?: number;
  hours?: number;
}

// ============= Skill Execution APIs =============

export const getSkillExecutions = async (params?: QueryParams) => {
  const response = await apiClient.get<{
    total: number;
    records: SkillExecutionRecord[];
  }>('/classification-analytics/skill-executions', { params });
  return response.data;
};

export const getSkillExecutionStats = async (params?: { hours?: number; user_id?: string }) => {
  const response = await apiClient.get<SkillExecutionStats>(
    '/classification-analytics/skill-executions/stats',
    { params }
  );
  return response.data;
};

export const exportSkillExecutions = async (
  format: 'csv' | 'json',
  params?: Omit<QueryParams, 'offset'>
) => {
  const response = await apiClient.get(
    '/classification-analytics/export/skill-executions',
    {
      params: { ...params, format },
      responseType: 'blob',
    }
  );

  // Create download link
  const url = window.URL.createObjectURL(new Blob([response.data]));
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', `skill_executions.${format}`);
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};

// ============= Classification Log APIs =============

export const getClassificationLogs = async (params?: QueryParams) => {
  const response = await apiClient.get<{
    total: number;
    records: ClassificationLogRecord[];
  }>('/classification-analytics/classifications', { params });
  return response.data;
};

export const getClassificationStats = async (params?: {
  hours?: number;
  user_id?: string;
  agent_type?: string;
}) => {
  const response = await apiClient.get<ClassificationStats>(
    '/classification-analytics/classifications/stats',
    { params }
  );
  return response.data;
};

export const exportClassificationLogs = async (
  format: 'csv' | 'json',
  params?: Omit<QueryParams, 'offset'>
) => {
  const response = await apiClient.get(
    '/classification-analytics/export/classifications',
    {
      params: { ...params, format },
      responseType: 'blob',
    }
  );

  // Create download link
  const url = window.URL.createObjectURL(new Blob([response.data]));
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', `classification_logs.${format}`);
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};
